import React, { useState, useRef } from 'react';
import { X, Upload, FileCheck, AlertCircle } from 'lucide-react';

const BolUploadModal = ({ isOpen, onClose, onUpload, onSkip, truckId, truckInfo, hideBolNumber = false }) => {
  const [bolFile, setBolFile] = useState(null);
  const [bolFileName, setBolFileName] = useState('');
  const [bolNumber, setBolNumber] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'image/tiff'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Only PDF, Word documents, and images are allowed.');
        return;
      }
      
      setError('');
      setBolFile(file);
      setBolFileName(file.name);
    }
  };

  // Trigger file input click
  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileChange({ target: { files } });
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!bolFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      await onUpload(truckId, bolFile, bolNumber.trim() || null);
      // Success - close modal
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to upload BOL file');
      setIsUploading(false);
    }
  };

  // Handle skip — complete the truck without a BOL file.
  const handleSkip = async () => {
    if (!onSkip) return;
    setIsSkipping(true);
    setError('');
    try {
      await onSkip(truckId, bolNumber.trim() || null);
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to complete without BOL');
      setIsSkipping(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setBolFile(null);
    setBolFileName('');
    setBolNumber('');
    setError('');
    setIsUploading(false);
    setIsSkipping(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-lg max-w-md w-full shadow-xl">
        <div className="flex justify-between items-center p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-slate-200">
            {truckInfo?.bol_file_path ? 'Replace' : 'Upload'} Bill of Lading (BOL)
          </h3>
          <button
            className="text-slate-400 hover:text-slate-200"
            onClick={handleClose}
            disabled={isUploading || isSkipping}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4">
          {/* Truck info */}
          {truckInfo && (
            <div className="mb-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="text-sm text-slate-400">Uploading BOL for:</div>
              <div className="text-slate-200 font-medium">{truckInfo.carrier}</div>
              <div className="text-sm text-slate-400">
                {truckInfo.customer && `Customer: ${truckInfo.customer}`}
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" />
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tif,.tiff"
            className="hidden"
          />

          {/* Upload area */}
          <div 
            className="border-dashed border-2 border-slate-600 rounded-lg p-6 mb-4 text-center cursor-pointer hover:border-slate-500 transition-colors"
            onClick={handleBrowseClick}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {bolFile ? (
              <div className="flex flex-col items-center">
                <FileCheck className="w-12 h-12 text-green-500 mb-3" />
                <p className="text-sm text-slate-300 break-all mb-2">{bolFileName}</p>
                <p className="text-xs text-slate-400">
                  Click to replace file
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="w-12 h-12 text-slate-500 mb-3" />
                <p className="text-sm text-slate-400 mb-2">
                  Drag & drop your BOL file here, or click to browse
                </p>
                <p className="text-xs text-slate-500">
                  Supported formats: PDF, Word, Images (Max 10MB)
                </p>
              </div>
            )}
          </div>

          {/* BOL number (optional). Hidden when the parent has already collected this value
              elsewhere — e.g. the wizard's "BOL / Pro Number" input on the cost step. */}
          {!hideBolNumber && (
            <div className="mb-2">
              <label className="block text-sm text-slate-300 mb-1" htmlFor="bol-number-input">
                BOL Number <span className="text-slate-500 text-xs">(optional)</span>
              </label>
              <input
                id="bol-number-input"
                type="text"
                value={bolNumber}
                onChange={(e) => setBolNumber(e.target.value)}
                disabled={isUploading || isSkipping}
                placeholder="e.g. BOL-12345"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="p-4 border-t border-slate-700 flex justify-end space-x-3">
          <button
            type="button"
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md text-sm disabled:opacity-50"
            onClick={handleClose}
            disabled={isUploading || isSkipping}
          >
            Cancel
          </button>
          {onSkip && (
            <button
              type="button"
              className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-amber-50 rounded-md text-sm disabled:opacity-50"
              onClick={handleSkip}
              disabled={isUploading || isSkipping}
            >
              {isSkipping ? 'Completing...' : 'Skip & Complete Without BOL'}
            </button>
          )}
          <button
            type="button"
            className={`px-4 py-2 rounded-md text-white text-sm ${
              bolFile && !isUploading && !isSkipping
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gray-600 cursor-not-allowed'
            }`}
            onClick={handleUpload}
            disabled={!bolFile || isUploading || isSkipping}
          >
            {isUploading ? 'Uploading...' : 'Upload BOL'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BolUploadModal;